/**
 * Layout primitives for the 2848 replica.
 *
 * The official form is a fixed-layout page, so the replica is too: every
 * element is placed at the coordinate measured out of the PDF rather than
 * being flowed and nudged. `pt()` converts PDF points to px through the
 * --f2848px custom property set on .f2848-page, so one number controls the scale.
 *
 * See f2848-rules.ts for the measured rules and f2848-geometry.ts for the width.
 *
 * Two html2canvas constraints shape what is allowed here, both learned the
 * hard way on the W-7: it has no support for CSS gradients, and it drops SVG
 * used as a background-image. Anything drawn must therefore be a solid
 * background, a border, or an <img>.
 */
import { CSSProperties, ReactNode, useLayoutEffect, useRef, useState } from "react";
import { DASH_LEN, DASH_PITCH } from "./f2848-rules";

/** PDF points -> css length at the page's current scale. */
export const pt = (n: number) => `calc(${n} * var(--f2848px))`;

/** The wordmark face — a separate condensed design, not a heavier body weight. */
export const FONT_WORDMARK = '"HelveticaNeueLTStd-BlkCn", "Helvetica Neue", Arial, sans-serif';
/** The title face. */
export const FONT_TITLE = '"ITCFranklinGothicStd-Demi", "Helvetica Neue", Arial, sans-serif';

/**
 * Rule weights, in FORM points via pt() — not CSS `pt`.
 *
 * CSS pt is an absolute unit: 0.5pt is 0.67px no matter how large the page is
 * rendered, so a rule written that way stays a 1px hairline at 2100px wide and
 * rasterises to uneven grey. Going through pt() ties the weight to --f2848px, so
 * it scales with everything else.
 *
 * These are the official's OWN weights, read out of the PDF's vector stream:
 * 95 of page 1's 106 lines are 0.5pt, four are 1.0, and page 2 has one at
 * 0.75. They used to be set 1.5x heavier than that, which is what made the
 * whole sheet read bolder than the form it copies — the page's ink came out
 * 1.23x the official's.
 */
export const RULE_PT = 0.5;
export const RULE_W = `${pt(RULE_PT)} solid #000`;
/**
 * Half a rule's weight, in points. The measured y of a rule is where the
 * official draws the STROKE, so a rule whose top edge sits on that number ends
 * up half its weight too low — enough to read as the type crowding the rule
 * above it. Offsetting by half the weight centres the stroke on the
 * measurement instead.
 */
const RULE_HALF = RULE_PT / 2;

/** The sheet's width in form points — the reference the scale is taken against. */
const PAGE_W_PT = 612;

/**
 * How far a fit may move a run before it is treated as a bad measurement
 * rather than a metric correction.
 */
const FIT_MIN = 0.7;
const FIT_MAX = 1.3;

/**
 * The page's line-height, mirrored from .f2848-page in w-2484Style.css.
 *
 * Half of a line box's leading sits above the type, so this is what decides
 * where a run's first line lands inside its cell. A block that sets its own
 * `leading` therefore starts higher than its neighbours unless it is padded
 * back down by the difference — which is what PAGE_LEADING is for.
 */
const PAGE_LEADING = 1.5;
/** The page's body size, in points — the default when a run sets no `size`. */
const BODY_PT = 8;

/* --------------------------------------------------------------------------
 * Where type sits inside its line box
 * --------------------------------------------------------------------------
 * `At` positions a run by the TOP OF ITS LINE BOX, which is taller than the
 * type in it: half the leading sits above the caps and the descender space
 * below the baseline. Anything that has to line up with the type itself — a
 * checkbox, most obviously — has to know how far down inside that box the type
 * actually is, so these are the numbers that say.
 *
 * All three come out of HelveticaNeueLTStd-Roman.otf itself rather than being
 * assumed: hhea ascender/descender 0.714 / -0.286 (so the content box is
 * exactly 1em) and OS/2 sxHeight 517/1000. Every cut of the family carries the
 * same numbers.
 */
const ASCENT = 0.714;
const CONTENT = 1.0;
const X_HEIGHT = 0.517;

/**
 * Distance, in points, from a run's line-box top down to the optical middle of
 * its type — the height a checkbox has to sit at to read as level with a label.
 *
 *   halfLeading = size * (PAGE_LEADING - CONTENT) / 2   // above the caps
 *   baseline    = halfLeading + size * ASCENT
 *   middle      = baseline - size * X_HEIGHT / 2
 *
 * The x-band, not the cap band. In a label like "Construction" only the C
 * reaches cap height and the other eleven letters stop at the x-height, so the
 * line's visual mass sits well below the midpoint of cap-to-baseline: centring
 * a box there puts it 0.098em — 0.79pt at 8pt — above where the word looks like
 * its middle, which is enough to read as the box floating over its label.
 */
export const runOpticalMiddle = (size: number = BODY_PT): number =>
  (size * (PAGE_LEADING - CONTENT)) / 2 + size * ASCENT - (size * X_HEIGHT) / 2;

type AtProps = {
  x: number;
  y: number;
  w?: number;
  h?: number;
  /** points; omit to inherit the page's 8pt body */
  size?: number;
  bold?: boolean;
  italic?: boolean;
  align?: CSSProperties["textAlign"];
  /** defaults to true — measured runs are single lines unless said otherwise */
  nowrap?: boolean;
  /** leading in points, for the few multi-line blocks */
  leading?: number;
  /** horizontal squeeze for runs the substituted face still sets too wide */
  condense?: number;
  /**
   * The run's measured ink width on the official, in points. Set this and the
   * run is scaled horizontally to match it exactly, which is the only way to
   * reproduce the official's line widths with a substituted face — and it also
   * reproduces the handful of lines the official justifies out to fill their
   * column.
   */
  fit?: number;
  className?: string;
  style?: CSSProperties;
  children?: ReactNode;
};

/** Absolutely places content at a measured point coordinate. */
export const At = ({
  x,
  y,
  w,
  h,
  size,
  bold,
  italic,
  align,
  nowrap = true,
  leading,
  condense,
  fit,
  className,
  style,
  children,
}: AtProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const [autoScale, setAutoScale] = useState<number | null>(null);
  const centred = align === "center";

  /**
   * Scale the run horizontally so its ink matches the official's.
   *
   * The ink is measured with a Range rather than offsetWidth: offsetWidth is
   * the BOX, and a run that declares a measured column width `w` is usually
   * far narrower than that box, so fitting to it would squeeze the run to the
   * column instead of to the official's line width.
   *
   * The transform is cleared for the measurement. A Range rect DOES include
   * the element's own transform, so measuring through it would feed the last
   * scale back into the next one and the run would oscillate instead of
   * settling. Clearing it inside a layout effect happens before paint, so
   * nothing flickers, and the ratio is always taken against the natural width.
   */
  useLayoutEffect(() => {
    const el = ref.current;
    if (!fit || !el) return;
    let cancelled = false;

    const apply = () => {
      if (cancelled || !ref.current) return;
      const node = ref.current;
      // px per form-point, taken from the PAGE'S OWN RECT rather than the
      // --f2848px custom property.
      //
      // The sheet is rendered inside a wrapper carrying a CSS `zoom` (.f2848-screen
      // scales it down so it doesn't dwarf the UI). getBoundingClientRect
      // returns ZOOMED pixels; a custom property does not scale, so measuring
      // the ink through one and the target through the other put every ratio
      // out by 1/zoom — about 1.47x — which the clamp below then rejected. The
      // result was that NO run was fitted on screen, every label sat at its
      // natural width, and long ones ran into the next column. Deriving the
      // scale from the page element measures both sides in the same space, so
      // it is right at any zoom.
      const page = node.closest(".f2848-page") as HTMLElement | null;
      const ppp = page ? page.getBoundingClientRect().width / PAGE_W_PT : 0;
      if (!ppp) return;
      const applied = node.style.transform;
      node.style.transform = "none";
      const range = document.createRange();
      range.selectNodeContents(node);
      const ink = range.getBoundingClientRect();
      const box = node.getBoundingClientRect();
      node.style.transform = applied;
      if (!ink.width) return;
      const natural = centred ? ink.width : ink.right - box.left;
      if (natural <= 0) return;
      const scale = (fit * ppp) / natural;
      // A declared text box in the PDF can be much wider than the ink it holds
      // — "Date" in the 2848's IRS-use panel spans its whole leader area, for
      // instance. Fitting to that would stretch the run ~2.8x. Only honour a
      // fit that is a plausible metric correction; outside this band the run
      // is left at its natural width.
      if (scale < FIT_MIN || scale > FIT_MAX) return;
      setAutoScale(scale);
    };

    apply();

    // Measure AGAIN once the IRS faces are in. `font-display: block` keeps the
    // fallback from being painted, but it does not keep it out of LAYOUT: this
    // first pass runs while the block period is still on, so it measures
    // Arial's metrics and would fit every run to the wrong natural width.
    //
    // Both hooks are needed. fonts.ready alone is not enough: it resolves as
    // soon as nothing is pending, and at first layout the browser has often
    // not started fetching the faces yet — so it fires immediately, still
    // against the fallback, and the run is left stretched. `loadingdone`
    // catches the batch that starts after that, which is the one that matters.
    const fonts = (document as any).fonts as FontFaceSet | undefined;
    if (fonts) {
      fonts.ready.then(apply);
      fonts.addEventListener?.("loadingdone", apply);
    }
    return () => {
      cancelled = true;
      fonts?.removeEventListener?.("loadingdone", apply);
    };
  }, [fit, size, centred, children]);

  const scale = condense ?? autoScale ?? undefined;

  return (
  <div
    ref={ref}
    className={className}
    style={{
      position: "absolute",
      left: pt(x),
      top: pt(y),
      ...(w !== undefined ? { width: pt(w) } : null),
      ...(h !== undefined ? { height: pt(h) } : null),
      ...(size !== undefined ? { fontSize: pt(size) } : null),
      // A block that sets its own pitch keeps it, but is padded back down by
      // the half-leading it gave up, so its first line sits on the same
      // baseline the page would have given it. Without this every leaded block
      // rides up out of its cell.
      ...(leading !== undefined
        ? {
            lineHeight: pt(leading),
            paddingTop: pt(
              Math.max(0, ((size ?? BODY_PT) * PAGE_LEADING - leading) / 2),
            ),
          }
        : null),
      ...(bold ? { fontWeight: 700 } : null),
      ...(italic ? { fontStyle: "italic" } : null),
      ...(align ? { textAlign: align } : null),
      whiteSpace: nowrap ? "nowrap" : "normal",
      ...(scale
        ? {
            transform: `scaleX(${scale})`,
            transformOrigin: centred ? "top center" : "left top",
          }
        : null),
      ...style,
    }}
  >
    {children}
  </div>
  );
};

/**
 * A checkbox, centred on the run it reads with.
 *
 * `y` is the y of THAT RUN — the same coordinate its <At> takes — not the box's
 * own top edge, and the box works out its own position from it. That is a
 * deliberate departure from the official, which sets its boxes as if they were
 * characters sitting on the baseline: 8pt tall on 8pt type, so a box reaches
 * 2.3pt above the caps and stops level with the baseline, and reads about a
 * point high with visibly more white beneath it than above. Splitting that
 * overhang evenly puts the box's middle on the middle of the caps instead.
 *
 * It also removes the drift that came of placing box and label from
 * separately measured coordinates: whatever the run does — including the lifts
 * in each page — the box now follows it exactly.
 */
export const Cb = ({
  x,
  y,
  size = 8,
  run = BODY_PT,
  weight = RULE_PT,
  checked,
}: {
  x: number;
  /** the y of the run this box sits beside, NOT the box's own top edge */
  y: number;
  /** the form uses 7, 8 and 8.5pt boxes — pass the measured one */
  size?: number;
  /** type size of the run being aligned to; defaults to the 8pt body */
  run?: number;
  /**
   * Stroke weight in points. The official draws 42 of its 44 boxes as 0.5pt
   * hairlines and only two — the "Check if to be sent copies of notices and
   * communications" boxes beside representatives 1 and 2 — at 1.5pt. Drawing
   * them all at 1.5 made every box on the sheet read as bold.
   */
  weight?: number;
  checked?: boolean;
}) => (
  <div
    style={{
      position: "absolute",
      left: pt(x),
      top: pt(y + runOpticalMiddle(run) - size / 2),
      width: pt(size),
      height: pt(size),
      border: `${pt(weight)} solid #000`,
      background: "#fff",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: pt(size - 1),
      lineHeight: 1,
      fontWeight: 700,
      color: "#2977cc",
    }}
  >
    {checked ? "✓" : ""}
  </div>
);

/**
 * A fill-in field: an <input> for the browser, plus a plain span carrying the
 * same text for the capture.
 *
 * The span is not belt-and-braces. html2canvas does not use the browser's
 * layout for input values — it draws them itself, from the top of the element
 * box, ignoring line-height and padding, which sliced the ascenders off every
 * value in the PDF while the browser showed them perfectly centred. A span is
 * ordinary text, which html2canvas does render correctly, so .is-capturing
 * hides the input's own text and shows this instead.
 */
export const Field = ({
  x,
  y,
  w,
  h = 10,
  ...rest
}: {
  x: number;
  y: number;
  w: number;
  h?: number;
} & React.InputHTMLAttributes<HTMLInputElement>) => (
  <>
    <input
      type="text"
      {...rest}
      style={{
        position: "absolute",
        left: pt(x),
        top: pt(y),
        width: pt(w),
        height: pt(h),
        ...rest.style,
      }}
    />
    <span
      className="f2848-print-value"
      style={{
        position: "absolute",
        left: pt(x),
        top: pt(y),
        width: pt(w),
        height: pt(h),
        // a line box the height of the field centres the text the same way
        // the browser centres the input's own value
        lineHeight: pt(h),
        paddingLeft: pt(2),
        // text styling set on the input must be mirrored here: the capture
        // renders this span, not the input, so anything left behind vanishes
        // from the PDF while still looking right in the browser.
        fontWeight: rest.style?.fontWeight,
        textAlign: rest.style?.textAlign,
        fontStyle: rest.style?.fontStyle,
      }}
    >
      {typeof rest.value === "string" || typeof rest.value === "number"
        ? rest.value
        : null}
    </span>
  </>
);

/** The solid ▶ the official uses to point at a write-in area. */
export const Arrow = ({
  x,
  y,
  size = 5,
}: {
  x: number;
  y: number;
  size?: number;
}) => (
  <div
    style={{
      position: "absolute",
      left: pt(x),
      top: pt(y),
      width: 0,
      height: 0,
      borderTop: `${pt(size / 2)} solid transparent`,
      borderBottom: `${pt(size / 2)} solid transparent`,
      borderLeft: `${pt(size * 0.8)} solid #000`,
    }}
  />
);

/**
 * Draws a measured set of rules. Passing the extracted arrays straight through
 * means the replica's rule structure is the official's by construction — there
 * is nothing to keep in sync by hand.
 */
export const Rules = ({
  h,
  v,
  dashed = [],
  hWeights = {},
  vWeights = {},
}: {
  h: readonly (readonly number[])[];
  v: readonly (readonly number[])[];
  /** write-in rules the official draws dashed rather than solid */
  dashed?: readonly (readonly number[])[];
  /** y -> stroke weight in points, for the few rules heavier than RULE_PT */
  hWeights?: Record<number, number>;
  /** x -> stroke weight in points */
  vWeights?: Record<number, number>;
}) => (
  <>
    {h.map(([y, x0, x1], i) => {
      const wt = hWeights[y] ?? RULE_PT;
      return (
        <div
          key={`h${i}`}
          style={{
            position: "absolute",
            left: pt(x0),
            top: pt(y - wt / 2),
            width: pt(x1 - x0),
            borderTop: `${pt(wt)} solid #000`,
            // above the fields' screen fill, which would
            // otherwise hide the rule under every input
            zIndex: 1,
          }}
        />
      );
    })}
    {dashed.flatMap(([y, x0, x1], i) => {
      // One element per dash. html2canvas supports neither border-style nor
      // gradients, so a dashed rule written either of those ways renders in
      // the browser and then vanishes from the capture.
      const out = [];
      for (let x = x0; x < x1 - 0.01; x += DASH_PITCH) {
        out.push(
          <div
            key={`d${i}-${out.length}`}
            style={{
              position: "absolute",
              left: pt(x),
              top: pt(y - RULE_HALF),
              width: pt(Math.min(DASH_LEN, x1 - x)),
              borderTop: RULE_W,
            }}
          />,
        );
      }
      return out;
    })}
    {v.map(([x, y0, y1], i) => {
      const wt = vWeights[x] ?? RULE_PT;
      return (
        <div
          key={`v${i}`}
          style={{
            position: "absolute",
            left: pt(x - wt / 2),
            top: pt(y0),
            height: pt(y1 - y0),
            borderLeft: `${pt(wt)} solid #000`,
            // above the fields' screen fill, which would
            // otherwise hide the rule under every input
            zIndex: 1,
          }}
        />
      );
    })}
  </>
);

/** Dot leaders (". . . . .") are type on the official, not a rule. */
export const Leader = ({ n }: { n: number }) => (
  <span style={{ letterSpacing: pt(2.6) }}>{". ".repeat(n).trim()}</span>
);


